import { useCurrenciesContext, useSecuritiesContext } from '../../contexts';
import { Box } from '../Core';
import { CurrencyRenderer } from '../CurrencyRenderer';
import { SecurityRenderer } from '../SecurityRenderer';

interface AssetRendererProps {
  asset: string;
  colorful?: boolean;
  showIcon?: boolean;
  showDescription?: boolean;
  iconSize?: number;
}

/**
 * Renders an asset (currency or security) from a symbol string.
 *
 * Resolution order:
 * 1. Currency — renders via {@link CurrencyRenderer} with icon
 * 2. Security — renders via {@link SecurityRenderer} with icon
 * 3. Fallback — renders the raw symbol as text
 *
 * @param asset - The asset symbol to resolve and render (e.g. `"BTC"`, `"BTC-USD-PERP"`).
 * @param colorful - Whether to render the asset icon in color. Passed through to the underlying renderer.
 * @param showIcon - Whether to show the asset icon. Passed through to the underlying renderer.
 * @param showDescription - Whether to show the asset description. Passed through to the underlying renderer.
 * @param iconSize - Override the icon size in pixels. Passed through to the underlying renderer.
 */
export const AssetRenderer = ({ asset, colorful, showIcon, showDescription, iconSize }: AssetRendererProps) => {
  const { currenciesBySymbol } = useCurrenciesContext();
  const { securitiesBySymbol } = useSecuritiesContext();

  const currency = currenciesBySymbol.get(asset);
  if (currency) {
    return (
      <CurrencyRenderer
        currency={currency}
        colorful={colorful}
        showIcon={showIcon}
        showDescription={showDescription}
        iconSize={iconSize}
      />
    );
  }

  const security = securitiesBySymbol.get(asset);
  if (security) {
    return (
      <SecurityRenderer
        security={security}
        colorful={colorful}
        showIcon={showIcon}
        showDescription={showDescription}
        iconSize={iconSize}
      />
    );
  }

  return (
    <Box color="colorTextImportant" fontSize="fontSizeMd">
      {asset}
    </Box>
  );
};
